import { ExternalLink, Link } from '@/components/Elements';
import { BookmarkButton } from '@/components/Elements/BookmarkButton';
import { Pagination } from '@/features/analyze/components/Pagination';
import {
  ActiveArticle,
  SelectedRangeCondition,
} from '@/features/analyze/hooks/useTransitionSelectedRangeResult';
import { PageInfo } from '@/types';
import { getSafeExternalUrl } from '@/utils/url';
import { format } from '@formkit/tempo';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { tv } from 'tailwind-variants';
import { BookOpenIcon } from '@heroicons/react/24/outline';

const cellClassName = tv({
  base: 'p-2',
  variants: {
    type: {
      column: 'whitespace-nowrap text-start text-sm uppercase text-gray-500',
      normal: 'text-sm text-gray-800',
    },
  },
});

type HeatmapSearchResultProps = {
  docs?: ActiveArticle[];
  pageInfo?: PageInfo;
  selectedRangeCondition: SelectedRangeCondition | null;
  setSelectedRangeCondition: Dispatch<
    SetStateAction<SelectedRangeCondition | null>
  >;
};

export const HeatmapSearchResult = ({
  docs,
  selectedRangeCondition,
  pageInfo,
  setSelectedRangeCondition,
}: HeatmapSearchResultProps) => {
  const { t } = useTranslation();

  const onChangePage = (pageStart: number) => {
    setSelectedRangeCondition((prev) => {
      if (!prev) return null;
      return { ...prev, offset: pageStart };
    });
  };

  return (
    <div className="w-full">
      <h3 className="py-2 text-start text-gray-600">
        {t('選択中の範囲の記事')}
      </h3>
      {selectedRangeCondition && (
        <p className="mb-2 text-start text-sm text-gray-600">
          {t('国名: {{country}}', { country: selectedRangeCondition.country })}
          <br />
          {t('期間: {{start}} ~ {{end}}', {
            start: format(selectedRangeCondition.publishedStartAt, {
              date: 'short',
            }),
            end: format(selectedRangeCondition.publishedEndAt, {
              date: 'short',
            }),
          })}
        </p>
      )}
      <table className="w-full border">
        <thead className="border-b border-b-gray-200 bg-gray-200">
          <tr>
            <th scope="col" className={cellClassName({ type: 'column' })}>
              {t('filter.label.date')}
            </th>
            <th scope="col" className={cellClassName({ type: 'column' })}>
              {t('filter.label.organization')}
            </th>
            <th scope="col" className={cellClassName({ type: 'column' })}>
              {t('タイトル')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {selectedRangeCondition &&
            docs &&
            docs.map((doc) => {
              const safeDocUrl = getSafeExternalUrl(doc.url);
              const detailPath = `/articles/${doc.code}?from=transition`;
              const iconClassName =
                'grid size-7 place-items-center rounded-full bg-rw-white transition-colors hover:bg-rw-white/80';
              return (
                <tr key={doc.id}>
                  <td className={cellClassName({ type: 'normal' })}>
                    {format(doc.publishedAt, { date: 'short' })}
                  </td>
                  <td className={cellClassName({ type: 'normal' })}>
                    {doc.organization}
                  </td>
                  <td
                    className={cellClassName({
                      type: 'normal',
                      className: 'h-min-content',
                    })}
                  >
                    <div className="flex w-full items-center gap-x-2">
                      <span className="flex-1 break-words">
                        {safeDocUrl ? (
                          <ExternalLink
                            to={safeDocUrl}
                            underline="hover"
                            className="leading-tight"
                          >
                            {doc.title}
                          </ExternalLink>
                        ) : (
                          <Link
                            to={`/articles/${doc.code}?from=transition`}
                            underline="hover"
                            className="leading-tight"
                          >
                            {doc.title}
                          </Link>
                        )}
                      </span>
                      <Link
                        to={detailPath}
                        className={iconClassName}
                        aria-label={t('記事詳細を開く')}
                        title={t('記事詳細を開く')}
                      >
                        <BookOpenIcon className="size-4 stroke-2 text-rw-sub" />
                      </Link>
                      <BookmarkButton
                        code={doc.code}
                        title={doc.title}
                        url={doc.url}
                        isBookmarked={doc.isBookmarkedByCurrentUser}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
      {!selectedRangeCondition && (
        <p className="my-3 text-sm">
          {t('上のヒートマップから範囲を選択してください。')}
        </p>
      )}
      {selectedRangeCondition && pageInfo && (
        <div className="mt-4 grid w-full place-content-center">
          <Pagination pageInfo={pageInfo} size="sm" onClick={onChangePage} />
        </div>
      )}
    </div>
  );
};
